/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { jsx } from '@atlaskit/css';
import {
	SkillTag,
	type RovoSkillTagProps,
} from '@atlassian/rovo-platform-ui-components/skills/SkillTag';
import { ConnectedSkillTag } from '@atlassian/rovo-skills/ui/connected-skill-tag';

/**
 * Creates the `nodes.skillTag` definition used by extension manifests to render
 * skill chip inline extensions. This is the **renderer only** — it does not
 * include quick-insert items and therefore does not need the skills list.
 *
 * Extracted so it can be shared between the full `useSkillsManifest` (which
 * also registers quick-insert items) and the lightweight
 * `useSkillTagExtensionProvider` (used as a Suspense fallback so skill chips
 * render immediately without waiting for the skills Relay query).
 */
export const createSkillTagNodeRenderer = (
	siteId?: string,
	disableSkillTagHoverCardInEditor: boolean = false,
	skillTagHoverCardMaxHeightInEditor?: number | string,
) => ({
	skillTag: {
		type: 'inlineExtension' as const,
		render:
			async (): Promise<
				(props: { node: { parameters?: Record<string, unknown> } }) => React.ReactElement | null
			> =>
			({ node }: { node: { parameters?: Record<string, unknown> } }) => {
				const params = node.parameters;
				if (!params) {
					return null;
				}
				// Use ConnectedSkillTag to fetch and render full metadata so the pill matches the typeahead style.
				if (siteId && !params.color && !params.iconName) {
					return (
						<ConnectedSkillTag
							slug={params.slug as string}
							siteId={siteId}
							disableHovercard={disableSkillTagHoverCardInEditor}
							hovercardMaxHeight={skillTagHoverCardMaxHeightInEditor}
						/>
					);
				}
				return (
					<SkillTag
						{...(params as RovoSkillTagProps)}
						disableHovercard={disableSkillTagHoverCardInEditor}
						hovercardMaxHeight={skillTagHoverCardMaxHeightInEditor}
					/>
				);
			},
	},
});
